import { Box, Theme, SxProps } from '@mui/material';
import { TIME_FORMAT } from '@schooly/constants';
import { format } from 'date-fns';
import { forwardRef, useEffect, useState } from 'react';

import { useScheduleGridContext } from './ScheduleGridContext';
import { TIME_COLUMN_Z_INDEX } from './ScheduleGridTimeColumn';
import { getMinutesSinceMidnight } from './utils';
import { SystemStyleObject } from '@mui/system';

const UPDATE_INTERVAL_MS = 60_000;

export interface ScheduleGridNowIndicatorProps {
  sx?: SxProps<Theme>;
}

function getCurrentTimeString(): string {
  return format(new Date(), TIME_FORMAT);
}

export const ScheduleGridNowIndicator = forwardRef<HTMLDivElement, ScheduleGridNowIndicatorProps>(
  function ScheduleGridNowIndicator({ sx }, ref) {
    const { getItemTop, timeRange } = useScheduleGridContext();
    const [now, setNow] = useState(getCurrentTimeString);

    useEffect(() => {
      const id = setInterval(() => setNow(getCurrentTimeString()), UPDATE_INTERVAL_MS);
      return () => clearInterval(id);
    }, []);

    const nowMinutes = getMinutesSinceMidnight(now);
    const startMinutes = getMinutesSinceMidnight(timeRange.start);
    const endMinutes = getMinutesSinceMidnight(timeRange.end);

    if (nowMinutes < startMinutes || nowMinutes > endMinutes) return null;

    const top = getItemTop(now);

    return (
      <Box
        ref={ref}
        sx={(theme) => ({
          position: 'absolute',
          top,
          left: 0,
          right: 0,
          height: '1px',
          backgroundColor: theme.palette.primary.main,
          zIndex: TIME_COLUMN_Z_INDEX + 1,
          pointerEvents: 'none',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: -4,
            top: -3,
            width: 7,
            height: 7,
            borderRadius: '50%',
            backgroundColor: 'inherit',
          },
          ...((typeof sx === 'function' ? sx(theme) : sx) as SystemStyleObject),
        })}
      />
    );
  },
);
